/**
 * SnapshotMismatchBanner — May 2026
 *
 * Tiny shared banner displayed when the chart-context snapshot
 * renderer (``specialty/index.js``) detects that a saved snapshot
 * (medication / diagnosis / vitals) belongs to a different patient
 * than the note's client.
 *
 * Two responsibilities:
 *   1. Render the amber warning text + a data-testid attribute the
 *      QA / testing agent can hook on.
 *   2. Fire a fire-and-forget POST to
 *      ``/api/admin/snapshot-mismatch-events`` so the admin
 *      dashboard can surface a "Notes with snapshot mismatches
 *      detected" view.  Deduped per (noteId, fieldType, fieldId,
 *      clientId) tuple at the module scope so re-renders of the
 *      same broken note don't spam the endpoint.
 *
 * The backend ALSO dedupes by the same tuple via an upsert, so even
 * if the module-level cache is cold (page reload) the row count
 * stays sane.
 */
import React, { useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache of already-reported (noteId, fieldType, fieldId,
// clientId) tuples for this browser session.  Stops the POST from
// firing repeatedly as the user toggles tabs in the note.
const _reported = new Set();

function _tupleKey(parts) {
  return parts.map(p => String(p == null ? '' : p)).join('|');
}

export function SnapshotMismatchBanner({
  noteId,
  fieldId,
  fieldType,
  expectedClientId,
  detectedClientIds,
  testId,
  message,
}) {
  // Snapshot the parts the effect needs in a ref so we don't depend
  // on referentially-unstable arrays (``detectedClientIds`` is
  // recomputed every render in the parent).
  const partsRef = useRef({
    noteId,
    fieldId,
    fieldType,
    expectedClientId,
    detectedClientIds: detectedClientIds || [],
  });
  partsRef.current = {
    noteId,
    fieldId,
    fieldType,
    expectedClientId,
    detectedClientIds: detectedClientIds || [],
  };

  useEffect(() => {
    const { noteId, fieldId, fieldType, expectedClientId, detectedClientIds } = partsRef.current;
    if (!noteId || !fieldType || !expectedClientId) return;
    const key = _tupleKey([noteId, fieldType, fieldId, expectedClientId]);
    if (_reported.has(key)) return;
    _reported.add(key);

    // Fire-and-forget; we never want a transient network failure to
    // produce a console error in front of the clinician.  401 / 403
    // (e.g., the magic-link client-portal session — not a staff
    // user) is also silently ignored.
    axios.post(`${API}/admin/snapshot-mismatch-events`, {
      note_id: noteId,
      field_id: fieldId || null,
      field_type: fieldType,
      expected_client_id: expectedClientId,
      detected_client_ids: detectedClientIds || [],
    }).catch(() => { /* intentional silent */ });
  }, [noteId, fieldId, fieldType, expectedClientId]);

  return (
    <div
      className="mt-1 mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
      data-testid={testId || 'snapshot-mismatch-warning'}
    >
      ⚠️ {message}
    </div>
  );
}

export default SnapshotMismatchBanner;
